import React, { useState } from 'react'
import { useNewTranslation } from 'hooks'
import { CAMPAIGN_STATUS_ENUM, CampaignState } from 'state/campaigns'
import { CampaignManagement, TemplatedCampaignManagement } from 'components/Shared/Permission/enums'
import { useCheckSomePermissions } from 'hooks/permissions'
import IconButton from 'particles/Button/IconButton'
import Menu from 'particles/Menu'
import MenuItem from 'particles/MenuItem'
import { MenuItemProps } from 'particles/MenuItem/Text'
import { useSelector } from 'react-redux'
import { selectIsInternalUser } from 'state/profile'

interface CampaignCardProps {
    campaign: CampaignState
    onDuplicate: (uuid: string) => void
    onDelete: (campaign: CampaignState) => void
}

interface CardMenuItemProps extends MenuItemProps {
    show: boolean
}

const CampaignActions: React.FC<CampaignCardProps> = ({ campaign, onDuplicate, onDelete }) => {
    const [t] = useNewTranslation()
    const hasCreateNewCampaignPermission = useCheckSomePermissions([
        CampaignManagement.CreateAndEditBriefsForInternal,
        CampaignManagement.CreateAndEditBriefsForExternal,
        TemplatedCampaignManagement.CreateAndEditBriefsForInternal,
        TemplatedCampaignManagement.CreateAndEditBriefsForExternal,
    ])
    const isInternalUser = useSelector(selectIsInternalUser)

    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget)
    }

    const handleCloseMenu = () => {
        setMenuAnchorEl(null)
    }

    const isCampaignDeletable =
        campaign && [CAMPAIGN_STATUS_ENUM.DRAFT, CAMPAIGN_STATUS_ENUM.FOR_EDIT].includes(campaign.status)
    const canDeleteCampaign = isInternalUser ? true : campaign?.self_serve

    const menuItems: CardMenuItemProps[] = [
        {
            text: t('button.duplicateCampaign'),
            icon: 'copy',
            onClick: () => {
                handleCloseMenu()
                onDuplicate(campaign.uuid)
            },
            show: true,
        },
        {
            text: t('button.deleteCampaign'),
            icon: 'xCircle',
            textColor: 'error',
            iconColor: 'error',
            onClick: () => {
                handleCloseMenu()
                onDelete(campaign)
            },
            show: isCampaignDeletable && canDeleteCampaign,
        },
    ]

    return (
        <>
            {hasCreateNewCampaignPermission && (
                <>
                    <IconButton
                        form="icon"
                        size="s"
                        color="neutral"
                        icon="dotsThreeOutline"
                        iconForm="fill"
                        onClick={handleOpenMenu}
                    />
                    <Menu
                        size="s"
                        open={Boolean(menuAnchorEl)}
                        anchorEl={menuAnchorEl}
                        onClose={handleCloseMenu}
                        width={180}
                    >
                        {menuItems.map(({ show, ...item }, index) => show && <MenuItem.Text key={index} {...item} />)}
                    </Menu>
                </>
            )}
        </>
    )
}

export default CampaignActions
